import React, { useEffect, useState } from 'react';
import supabase from '../../supabase';
import { PlusCircle, Search } from 'lucide-react';
import { PDFViewer } from '@react-pdf/renderer';
import QuotationPDF from '../Quotation/QuotationPDF';
import EditQuotePopup from './EditQuotePopup';
import QuoteTrackerTable from './QuoteTrackerTable';
import { pdf } from '@react-pdf/renderer';
import './QuoteTracker.css';

const QuoteTracker = ({ 
  onCreateQuotation, 
  onSwitchToCreateTab, 
  selectedQuoteData, 
  onClearSelection,
  // NEW: URL-based modal state props from Dashboard
  modalState,
  openModal,
  closeModal,
  // NEW: Filter prop from QuoteAnalyticGraph
  initialFilter
}) => {
  const [user, setUser] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [sendingQuotation, setSendingQuotation] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Edit Popup States
  const [editPopupOpen, setEditPopupOpen] = useState(false);
  const [selectedQuoteForEdit, setSelectedQuoteForEdit] = useState(null);

  // PDF Preview States
  const [previewQuote, setPreviewQuote] = useState(null);
  const [previewClient, setPreviewClient] = useState(null);
  const [previewUser, setPreviewUser] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pdfViewerKey, setPdfViewerKey] = useState(0);

  const filterOptions = ['All', 'Draft', 'Accepted', 'Not Accepted'];

  // NEW: Handle initial filter from QuoteAnalyticGraph
  useEffect(() => {
    if (initialFilter) {
      setStatusFilter(initialFilter);
    }
  }, [initialFilter]);

  // NEW: Handle URL-based modal state changes
  useEffect(() => {
    if (!modalState) return;

    const { action, id } = modalState;

    if (action === 'view' && id) {
      // Load quote for viewing
      loadQuoteById(id);
    } else if (action === 'edit' && id) {
      // Load quote for editing
      loadQuoteForEdit(id);
    } else {
      // No action - clear preview
      setPreviewQuote(null);
      setPreviewClient(null);
      setPreviewUser(null);
      setEditPopupOpen(false);
      setSelectedQuoteForEdit(null);
    }
  }, [modalState]);

  // NEW: Load quote by ID for preview
  const loadQuoteById = async (quoteId) => {
    setLoadingPreview(true);
    try {
      // First try to find by actual ID
      let { data: quote, error: quoteError } = await supabase
        .from('quotes')
        .select('*')
        .eq('id', quoteId)
        .single();

      // If not found by ID, it might be a slug - search in local quotes
      if (quoteError || !quote) {
        // Try to find in already loaded quotes by matching the slug pattern
        const foundQuote = quotes.find(q => {
          const clientName = clients[q.client_id] || 'client';
          const expectedSlug = `${createSlug(q.quotation_number)}-${createSlug(clientName)}`;
          return expectedSlug === quoteId || q.id === quoteId;
        });
        
        if (foundQuote) {
          quote = foundQuote;
        } else {
          console.error('Quote not found:', quoteError);
          closeModal();
          return;
        }
      }

      // Now call the existing processing logic with the quote
      await processQuoteForPreview(quote);
    } catch (err) {
      console.error('Error loading quote:', err);
      closeModal();
    }
  };

  // NEW: Load quote for editing
  const loadQuoteForEdit = async (quoteId) => {
    try {
      // First try to find in local quotes
      let quote = quotes.find(q => q.id === quoteId);
      
      // If not found by ID, try matching by slug
      if (!quote) {
        quote = quotes.find(q => {
          const clientName = clients[q.client_id] || 'client';
          const expectedSlug = `${createSlug(q.quotation_number)}-${createSlug(clientName)}`;
          return expectedSlug === quoteId;
        });
      }
      
      // If still not found, try database lookup
      if (!quote) {
        const { data, error } = await supabase
          .from('quotes')
          .select('*')
          .eq('id', quoteId)
          .single();
        
        if (error || !data) {
          console.error('Quote not found for editing:', error);
          closeModal();
          return;
        }
        quote = data;
      }

      setSelectedQuoteForEdit(quote);
      setEditPopupOpen(true);
    } catch (err) {
      console.error('Error loading quote for edit:', err);
      closeModal();
    }
  };

  // Helper function to create URL-friendly slug
  const createSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };

  // Process quote for preview (extracted from handleQuoteClick)
  const processQuoteForPreview = async (quote) => {
    try {
      const [
        { data: quoteData },
        { data: clientData },
        { data: userData },
        { data: quoteItemsData }
      ] = await Promise.all([
        supabase.from('quotes').select('*').eq('id', quote.id).single(),
        supabase.from('clients').select('*').eq('id', quote.client_id).single(),
        supabase.from('users').select('*').eq('id', quote.user_id).single(),
        supabase.from('quote_items').select('*').eq('quote_id', quote.id).order('created_at', { ascending: true })
      ]);

      const transformedQuoteData = {
        fromInfo: userData || userProfile || {
          company_name: '',
          name: '',
          address: '',
          phone: '',
          email: '',
          gst_number: '',
          logo_url: ''
        },
        selectedClient: clientData,
        newClient: null,
        quotationInfo: {
          number: quoteData.quotation_number || '',
          issueDate: quoteData.created_at || '',
          notes: quoteData.notes || '',
        },
        termsAndConditions: quoteData.terms_conditions ? quoteData.terms_conditions.split('|') : [],
        userProfile: userData || userProfile,
      };

      const transformedItems = quoteItemsData && quoteItemsData.length > 0 
        ? quoteItemsData.map((item, index) => ({
            product_name: item.p_name || '',
            description: item.description || '',
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            total_amount: parseFloat(item.total_value) || 0,
            stableKey: `item-${item.id}-${index}`
          }))
        : [{
            product_name: '',
            description: '',
            quantity: 1,
            unit_price: 0,
            total_amount: 0,
            stableKey: 'no-items'
          }];

      const totalAmount = parseFloat(quoteData.total_amount || 0);
      const discountPercentage = parseFloat(quoteData.discount || 0);
      const gstPercentage = parseFloat(quoteData.gst || 0);
      const igstPercentage = parseFloat(quoteData.i_gst || 0);

      const itemsSubtotal = transformedItems.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity) * parseFloat(item.unit_price));
      }, 0);

      let discountAmount = 0;
      let subtotalAfterDiscount = itemsSubtotal;
      
      if (discountPercentage > 0) {
        discountAmount = (itemsSubtotal * discountPercentage) / 100;
        subtotalAfterDiscount = itemsSubtotal - discountAmount;
      }
      
      let totalTaxAmount = 0;
      let sgstAmount = 0;
      let cgstAmount = 0;
      let igstAmount = 0;
      
      if (gstPercentage > 0) {
        const gstAmount = (subtotalAfterDiscount * gstPercentage) / 100;
        sgstAmount = gstAmount / 2;
        cgstAmount = gstAmount / 2;
        totalTaxAmount += gstAmount;
      }
      
      if (igstPercentage > 0) {
        igstAmount = (subtotalAfterDiscount * igstPercentage) / 100;
        totalTaxAmount += igstAmount;
      }
      
      const transformedSummary = {
        subtotal: itemsSubtotal,
        totalTax: totalTaxAmount,
        total: totalAmount,
        sgstTotal: sgstAmount,
        cgstTotal: cgstAmount,
        igstTotal: igstAmount,
        discountAmount: discountAmount,
        subtotalAfterDiscount: subtotalAfterDiscount,
        Discount: discountAmount
      };

      const getLocalItemTotal = (item) => {
        return parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0);
      };

      const calculateLocalItemTax = (item) => {
        const itemBaseAmount = getLocalItemTotal(item);
        const itemAfterDiscount = itemBaseAmount - (discountPercentage > 0 ? (itemBaseAmount * discountPercentage) / 100 : 0);
        
        let itemTax = 0;
        if (gstPercentage > 0) {
          itemTax += (itemAfterDiscount * gstPercentage) / 100;
        }
        if (igstPercentage > 0) {
          itemTax += (itemAfterDiscount * igstPercentage) / 100;
        }
        
        return itemTax;
      };

      const taxType = igstPercentage > 0 ? 'IGST' : (gstPercentage > 0 ? 'SGST_CGST' : 'NONE');
      const sgstRate = gstPercentage / 2;
      const cgstRate = gstPercentage / 2;
      const igstRate = igstPercentage;
      
      setPreviewQuote({
        ...quoteData,
        items: transformedItems,
        quotationData: transformedQuoteData,
        localSummary: transformedSummary,
        getLocalItemTotal,
        calculateLocalItemTax,
        showTaxPerItemColumn: totalTaxAmount > 0,
        showHsnColumn: true,
        hasTax: totalTaxAmount > 0,
        hasdiscount: discountAmount > 0,
        discount: discountPercentage,
        taxType,
        sgstRate,
        cgstRate,
        igstRate,
      });
      
      setPreviewClient(clientData);
      setPreviewUser(userData);
      setPdfViewerKey(k => k + 1);
      
    } catch (err) {
      console.error('Error processing quote for preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Check if device is mobile/tablet
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(() => {
        const input = document.querySelector('.quotetrack-search-input');
        if (input) input.focus();
      }, 150);
    } else {
      setSearchQuery('');
    }
  };

  const handleInputBlur = (e) => {
    if (!e.relatedTarget || !e.relatedTarget.closest('.quotetrack-search-icon')) {
      if (!searchQuery.trim()) {
        setIsSearchOpen(false);
      }
    }
  };

  const handleSearchIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSearch();
  };

  // Handle selected quote data from Clients component
  useEffect(() => {
    if (selectedQuoteData && selectedQuoteData.quote) {
      handleQuoteClick(selectedQuoteData.quote);
      if (onClearSelection) {
        onClearSelection();
      }
    }
  }, [selectedQuoteData]);

  useEffect(() => {
    const fetchUserAndData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (!user) return;

      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('id, logo_url, subscription_id, quotation_prefix, email, company_name, name, address, phone, gst_number')
        .eq('id', user.id)
        .single();

      if (!profileError) setUserProfile(profile);

      await Promise.all([
        fetchAllQuotes(user.id),
        fetchAllClients(user.id)
      ]);
    };

    fetchUserAndData();
  }, []);

  useEffect(() => {
    filterQuotes();
  }, [quotes, searchQuery, statusFilter]);

  useEffect(() => {
    if (!loadingPreview && previewQuote) {
      setPdfViewerKey(k => k + 1);
    }
  }, [previewQuote, previewClient, previewUser, loadingPreview]);

  const fetchAllQuotes = async (userId) => {
    setLoading(true);
    try {
      const { data: quoteList, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setQuotes(quoteList || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllClients = async (userId) => {
    try {
      const { data: clientList, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', userId);
      if (error) throw error;
      const clientMap = {};
      clientList.forEach(c => clientMap[c.id] = c.client_name);
      setClients(clientMap);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const sendQuotationEmail = async (quote) => {
    if (!quote) return;
    const clientName = clients[quote.client_id] || 'Client';
    const quotationNumber = quote.quotation_number || 'Quotation';
    setSendingQuotation(prev => ({ ...prev, [quote.id]: true }));
    
    try {
      const { data: clientData, error: cErr } = await supabase
        .from('clients')
        .select('client_email, client_name')
        .eq('id', quote.client_id)
        .single();
      
      if (cErr) {
        throw new Error('Failed to fetch client information');
      }

      const clientEmail = clientData?.client_email;
      
      if (!clientEmail) {
        throw new Error(`No email address found for client: ${clientData?.client_name || clientName}`);
      }

      const [
        { data: quoteData },
        { data: userData },
        { data: quoteItemsData }
      ] = await Promise.all([
        supabase.from('quotes').select('*').eq('id', quote.id).single(),
        supabase.from('users').select('*').eq('id', quote.user_id).single(),
        supabase.from('quote_items').select('*').eq('quote_id', quote.id).order('created_at', { ascending: true })
      ]);

      const transformedQuoteData = {
        fromInfo: userData || userProfile || {
          company_name: '', name: '', address: '', phone: '', email: '', gst_number: '', logo_url: ''
        },
        selectedClient: clientData,
        newClient: null,
        quotationInfo: {
          number: quoteData.quotation_number || '',
          issueDate: quoteData.created_at || '',
          notes: quoteData.notes || '',
        },
        termsAndConditions: quoteData.terms_conditions ? quoteData.terms_conditions.split('|') : [],
        userProfile: userData || userProfile,
      };

      const transformedItems = quoteItemsData && quoteItemsData.length > 0 
        ? quoteItemsData.map((item, index) => ({
            product_name: item.p_name || '',
            description: item.description || '',
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            total_amount: parseFloat(item.total_value) || 0,
            stableKey: `item-${item.id}-${index}`
          }))
        : [{ product_name: 'No items found', description: '', quantity: 1, unit_price: 0, total_amount: 0, stableKey: 'no-items' }];

      const totalAmount = parseFloat(quoteData.total_amount || 0);
      const discountPercentage = parseFloat(quoteData.discount || 0);
      const gstPercentage = parseFloat(quoteData.gst || 0);
      const igstPercentage = parseFloat(quoteData.i_gst || 0);

      const itemsSubtotal = transformedItems.reduce((sum, item) => sum + (parseFloat(item.quantity) * parseFloat(item.unit_price)), 0);

      let discountAmount = 0;
      let subtotalAfterDiscount = itemsSubtotal;
      if (discountPercentage > 0) {
        discountAmount = (itemsSubtotal * discountPercentage) / 100;
        subtotalAfterDiscount = itemsSubtotal - discountAmount;
      }
      
      let totalTaxAmount = 0, sgstAmount = 0, cgstAmount = 0, igstAmount = 0;
      if (gstPercentage > 0) {
        const gstAmt = (subtotalAfterDiscount * gstPercentage) / 100;
        sgstAmount = gstAmt / 2;
        cgstAmount = gstAmt / 2;
        totalTaxAmount += gstAmt;
      }
      if (igstPercentage > 0) {
        igstAmount = (subtotalAfterDiscount * igstPercentage) / 100;
        totalTaxAmount += igstAmount;
      }
      
      const transformedSummary = {
        subtotal: itemsSubtotal, totalTax: totalTaxAmount, total: totalAmount,
        sgstTotal: sgstAmount, cgstTotal: cgstAmount, igstTotal: igstAmount,
        discountAmount, subtotalAfterDiscount, Discount: discountAmount
      };

      const getLocalItemTotal = (item) => parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0);
      const calculateLocalItemTax = (item) => {
        const base = getLocalItemTotal(item);
        const afterDisc = base - (discountPercentage > 0 ? (base * discountPercentage) / 100 : 0);
        let tax = 0;
        if (gstPercentage > 0) tax += (afterDisc * gstPercentage) / 100;
        if (igstPercentage > 0) tax += (afterDisc * igstPercentage) / 100;
        return tax;
      };

      const taxType = igstPercentage > 0 ? 'IGST' : (gstPercentage > 0 ? 'SGST_CGST' : 'NONE');

      const pdfDocument = (
        <QuotationPDF
          quotationData={transformedQuoteData}
          items={transformedItems}
          localSummary={transformedSummary}
          getLocalItemTotal={getLocalItemTotal}
          calculateLocalItemTax={calculateLocalItemTax}
          showTaxPerItemColumn={totalTaxAmount > 0}
          showHsnColumn={true}
          hasTax={totalTaxAmount > 0}
          hasdiscount={discountAmount > 0}
          discount={discountPercentage}
          taxType={taxType}
          sgstRate={gstPercentage / 2}
          cgstRate={gstPercentage / 2}
          igstRate={igstPercentage}
          selectedBank={null}
          qrCodeDataURL=""
          showValueColumn={true}
        />
      );

      const pdfBlob = await pdf(pdfDocument).toBlob();
      const pdfArrayBuffer = await pdfBlob.arrayBuffer();
      const pdfBase64 = btoa(String.fromCharCode(...new Uint8Array(pdfArrayBuffer)));

      const res = await fetch(
        'https://dzwtnmlmjwbwqevbhwnv.supabase.co/functions/v1/send-invoice-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabase.supabaseKey}` },
          body: JSON.stringify({
            from: 'no-reply@bossbyko.com',
            to: clientEmail,
            subject: `Quotation ${quotationNumber}`,
            html: `<p>Hello ${clientData?.client_name || clientName},</p><p>Please find attached quotation <strong>${quotationNumber}</strong>.</p><p>Best regards,<br>${userProfile?.company_name || userProfile?.name || 'Your Company'}</p>`,
            attachments: [{ filename: `Quotation_${quotationNumber}.pdf`, content: pdfBase64, encoding: 'base64', contentType: 'application/pdf' }]
          })
        }
      );

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to send email');
      
      const { error: updateErr } = await supabase.from('quotes').update({ email_sent: true }).eq('id', quote.id).eq('user_id', user.id);
      if (updateErr) throw updateErr;
      
      setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, email_sent: true } : q));
      
    } catch (err) {
      console.error('Send quotation error:', err);
      throw err;
    } finally {
      setSendingQuotation(prev => ({ ...prev, [quote.id]: false }));
    }
  };

  const filterQuotes = () => {
    let filtered = [...quotes];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(quote =>
        quote.quotation_number?.toLowerCase().includes(q) ||
        clients[quote.client_id]?.toLowerCase().includes(q) ||
        quote.p_name?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'All') {
      filtered = filtered.filter(quote => {
        if (statusFilter === 'Draft') return quote.email_sent !== true;
        const isAccepted = quote.status === true;
        return statusFilter === 'Accepted' ? isAccepted : !isAccepted;
      });
    }
    setFilteredQuotes(filtered);
  };

  const handleStatusUpdate = async (quoteId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [quoteId]: true }));
    try {
      const { error: uErr } = await supabase.from('quotes').update({ status: newStatus }).eq('id', quoteId).eq('user_id', user.id);
      if (uErr) throw uErr;
      setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
    } catch (err) {
      console.error('Error updating status:', err);
      throw err;
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  // UPDATED: Edit quote - now uses URL
  const handleEditQuote = (quote) => {
    const clientName = clients[quote.client_id] || 'client';
    openModal('edit', {
      id: quote.id,
      quotationNumber: quote.quotation_number,
      clientName: clientName
    });
  };

  // UPDATED: Close edit popup - now uses URL
  const handleCloseEditPopup = () => {
    setEditPopupOpen(false);
    setSelectedQuoteForEdit(null);
    closeModal();
  };

  const handleSaveQuote = async (updatedQuoteData) => {
    try {
      const { error } = await supabase.from('quotes').update(updatedQuoteData).eq('id', selectedQuoteForEdit.id).eq('user_id', user.id);
      if (error) throw error;
      setQuotes(prev => prev.map(q => q.id === selectedQuoteForEdit.id ? { ...q, ...updatedQuoteData } : q));
      handleCloseEditPopup();
    } catch (err) {
      console.error('Error updating quotation:', err);
      throw err;
    }
  };

  const deleteQuote = async (quote) => {
    try {
      const { error: deleteErr } = await supabase.from('quotes').delete().eq('id', quote.id).eq('user_id', user.id);
      if (deleteErr) throw deleteErr;
      setQuotes((prev) => prev.filter((q) => q.id !== quote.id));
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  };

  // UPDATED: Quote click handler - now uses URL navigation
  const handleQuoteClick = async (quote) => {
    const clientName = clients[quote.client_id] || 'client';
    openModal('view', {
      id: quote.id,
      quotationNumber: quote.quotation_number,
      clientName: clientName
    });
  };

  // UPDATED: Close preview - now uses URL
  const handleClosePreview = () => {
    setPreviewQuote(null);
    setPreviewClient(null);
    setPreviewUser(null);
    closeModal();
  };

  const handleCreateQuotationClick = () => {
    if (onSwitchToCreateTab) {
      onSwitchToCreateTab();
    } else if (onCreateQuotation) {
      onCreateQuotation();
    }
  };

  return (
    <>
      <div className="quotetrack-quote-management-container">
        <div className="quotetrack-quote-management-header">
          <div className="quotetrack-header-left">
            <h1 className="quotetrack-page-title">QUOTATIONS</h1>
          </div>
          <div className="quotetrack-header-right">
            {!isMobile && (
              <div className={`quotetrack-search-input-wrapper ${isSearchOpen ? 'search-open' : ''}`}>
                <span className="quotetrack-search-icon" onClick={handleSearchIconClick} tabIndex={0} role="button" aria-label="Toggle search">
                  <Search size={16} color='#000' />
                </span>
                <input
                  type="text"
                  placeholder="Search quotations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={handleInputBlur}
                  className="quotetrack-search-input"
                  aria-label="Search quotations"
                />
              </div>
            )}
            
            <button className="quotetrack-create-quote-btn" onClick={handleCreateQuotationClick} aria-label={isMobile ? "Create quotation" : "Create Quotation"}>
              {isMobile ? (<><PlusCircle size={20} /><span>Create Quotation</span></>) : (<>Create Quotation <PlusCircle size={18} className="add-icon" /></>)}
            </button>
          </div>
        </div>

        {isMobile && (
          <div className="quotetrack-mobile-search-container">
            <div className="quotetrack-mobile-search-wrapper">
              <Search size={16} color='#666' className="quotetrack-mobile-search-icon" />
              <input type="text" placeholder="Search quotations..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="quotetrack-mobile-search-input" aria-label="Search quotations" />
            </div>
          </div>
        )}

        <div className="quotetrack-filter-tabs">
          {filterOptions.map(opt => (
            <button key={opt} className={`quotetrack-filter-tab ${statusFilter === opt ? 'quotetrack-filter-tab-active' : ''}`} onClick={() => setStatusFilter(opt)}>{opt}</button>
          ))}
        </div>

        {loading && (
          <div className="quotetrack-loading-overlay">
            <div className="quotetrack-loading-content">
              <div className="quotetrack-loading-spinner" />
              <p className="quotetrack-loading-text">Loading...</p>
            </div>
          </div>
        )}

        <QuoteTrackerTable
          filteredQuotes={filteredQuotes}
          clients={clients}
          updatingStatus={updatingStatus}
          sendingQuotation={sendingQuotation}
          loading={loading}
          onQuoteClick={handleQuoteClick}
          onEditQuote={handleEditQuote}
          onStatusUpdate={handleStatusUpdate}
          onSendQuotationEmail={sendQuotationEmail}
          onDeleteQuote={deleteQuote}
          onCreateQuotationClick={handleCreateQuotationClick}
        />
      </div>

      {/* Edit Quote Popup */}
      {editPopupOpen && selectedQuoteForEdit && (
        <EditQuotePopup
          quote={selectedQuoteForEdit}
          clients={clients}
          onSave={handleSaveQuote}
          onClose={handleCloseEditPopup}
          userProfile={userProfile}
          isOpen={editPopupOpen}
        />
      )}
      
      {/* PDF Preview Popup - Now controlled by modalState */}
      {modalState?.action === 'view' && previewQuote && (
        <div className="quotetrack-pdf-popup-overlay" onClick={handleClosePreview}>
          <div className="quotetrack-pdf-popup" onClick={e => e.stopPropagation()}>
            <div className="invoice-table-pdf-popup-header">
              <h3>Quotation Preview - {previewQuote.quotation_number || 'Unknown'}</h3>
              <button className="quotetrack-pdf-popup-close" onClick={handleClosePreview}>×</button>
            </div>
            {loadingPreview ? (
              <div className="quotetrack-pdf-loading">
                <div className="quotetrack-loading-spinner"></div>
                <p>Loading quotation preview...</p>
              </div>
            ) : (
              <PDFViewer className="quotetrack-pdf-viewer" width="100%" height="100%" key={`pdf-viewer-${pdfViewerKey}`}>
                <QuotationPDF
                  quotationData={previewQuote.quotationData}
                  items={previewQuote.items}
                  localSummary={previewQuote.localSummary}
                  numberToWords={null}
                  getLocalItemTotal={previewQuote.getLocalItemTotal}
                  calculateLocalItemTax={previewQuote.calculateLocalItemTax}
                  showTaxPerItemColumn={previewQuote.showTaxPerItemColumn}
                  hasTax={previewQuote.hasTax}
                  hasdiscount={previewQuote.hasdiscount}
                  discount={previewQuote.discount}
                  discountType={previewQuote.discountType}
                  taxType={previewQuote.taxType}
                  sgstRate={previewQuote.sgstRate}
                  cgstRate={previewQuote.cgstRate}
                  igstRate={previewQuote.igstRate}
                  selectedBank={null}
                  qrCodeDataURL=""
                  showValueColumn={true}
                />
              </PDFViewer>
            )}
            <div className="quotetrack-pdf-popup-actions">
              <button className="quotetrack-button--popup" onClick={handleClosePreview}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuoteTracker;